import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { Pagination } from '../../../../components/Pagination';
import { SearchField } from '../../../../components/SearchField';

import {
  AddButton,
  Card,
  Container,
  Content,
  FilterPill,
  FilterRow,
  Header,
  PageSubtitle,
  PageTitle,
  TitleAccent,
} from './styles';

import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { AdminExercisesService } from '../../../../services/AdminExercisesService';
import { IExercise, IMuscleGroup } from '../../../../domains/Exercise';
import { getExercisePrimaryMedia } from '../../../../utils/getExercisePrimaryMedia';

const PAGE_SIZE = 10;

const AdminExercises: React.FC = () => {
  const adminExercisesService = new AdminExercisesService();

  const navigate = useNavigate();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [selectValue, setSelectValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const getMuscleGroups = useCallback(() => {
    adminExercisesService
      .getMuscleGroups()
      .then(response => {
        setMuscleGroups([{ id: '', name: 'Todos' }, ...response.data]);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast]);

  const handleRemoveExercise = useCallback(
    async (exercise_id: string) => {
      // Excluir um exercício da biblioteca padrão afeta TODAS as academias
      // que o usam hoje (as fichas que o referenciam somem em cascata) --
      // confirmação extra aqui, diferente da exclusão de um exercício
      // customizado de uma única academia.
      const confirmed = window.confirm(
        'Excluir este exercício da biblioteca padrão? Ele será removido das fichas de TODAS as academias que o utilizam hoje. Essa ação não pode ser desfeita.',
      );

      if (!confirmed) return;

      try {
        await adminExercisesService.removeExercise(exercise_id);

        setExercises(current =>
          current.filter(exercise => exercise.id !== exercise_id),
        );
        setTotal(current => current - 1);

        addToast({ type: 'success', title: 'Exercício removido' });
      } catch (err: any) {
        if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title:
              'Este exercício pertence a uma academia e não pode ser removido por aqui.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na remoção do exercício',
          description:
            'Houve um erro na remoção do exercício. Tente novamente mais tarde.',
        });
      }
    },
    [addToast],
  );

  const handleSelectMuscleGroup = useCallback((value: string) => {
    setSelectValue(value);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectValue]);

  useEffect(() => {
    setLoading(true);
    adminExercisesService
      .getExercises({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        muscle_group_id: selectValue || undefined,
      })
      .then(response => {
        setExercises(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos exercícios.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, selectValue]);

  useEffect(() => {
    getMuscleGroups();
  }, [getMuscleGroups]);

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>
              Biblioteca padrão de <TitleAccent>exercícios</TitleAccent>
            </PageTitle>
            <PageSubtitle>
              Exercícios visíveis para todas as academias da plataforma
            </PageSubtitle>
          </div>
          <AddButton
            type="button"
            onClick={() => navigate('/admin/exercises/new')}
          >
            <Icon name="add_circle" size={18} color={colors.onPrimary} />
            Novo exercício
          </AddButton>
        </Header>

        <FilterRow>
          <SearchField
            placeholder="Buscar exercício"
            value={searchText}
            onChange={setSearchText}
          />
          {muscleGroups.map(group => (
            <FilterPill
              key={group.id || 'all'}
              type="button"
              active={selectValue === group.id}
              onClick={() => handleSelectMuscleGroup(group.id as string)}
            >
              {group.name}
            </FilterPill>
          ))}
        </FilterRow>

        <Content>
          {exercises.map(exercise => {
            const primaryMedia = getExercisePrimaryMedia(exercise.media);

            return (
              <Card
                key={exercise.id}
                id={exercise.id}
                thumbnail_url={primaryMedia?.thumbnail_url}
                hasVideo={primaryMedia?.type === 'video'}
                exerciseName={exercise.name}
                muscleGroups={exercise.muscle_groups}
                description={exercise.description}
                isCustom={false}
                functionChangeButton={() =>
                  navigate(`/admin/exercises/${exercise.id}`, {
                    state: { exercise },
                  })
                }
                functionRemoveButton={() => handleRemoveExercise(exercise.id)}
              />
            );
          })}
        </Content>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          itemLabel="exercícios"
          onPageChange={setPage}
        />
      </Container>
    </Painel>
  );
};

export { AdminExercises };
